import React, { useEffect, useState } from 'react';
import CustomNavbar from '../CustomNavbar';
import api from '../axiosSetup';
import moment from 'moment';
import cfg from '../../config';

const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } });

const StatCard = ({ title, children }) => (
    <div className="card bg-secondary text-white mb-4">
        <div className="card-header fw-bold" style={{ background: 'rgba(0,0,0,0.25)' }}>{title}</div>
        <div className="card-body">{children}</div>
    </div>
);

const ZRStatistics = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);

    const year = new Date().getFullYear();

    useEffect(() => {
        api.get('/api/statistics-zunftrat', authHeader())
            .then(r => setData(r.data))
            .catch(() => setError('Fehler beim Laden der Statistiken'))
            .finally(() => setLoading(false));
    }, []);

    const downloadReport = async (selectedYear) => {
        setReportLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch(`/api/jahresbericht-pdf?year=${selectedYear}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.message || `HTTP ${res.status}`);
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `jahresbericht-${selectedYear}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            alert(`Fehler beim Herunterladen: ${err.message}`);
        } finally {
            setReportLoading(false);
        }
    };

    if (loading) return <div><CustomNavbar /><div className="container mt-4">Lädt…</div></div>;
    if (error) return <div><CustomNavbar /><div className="container mt-4 alert alert-danger">{error}</div></div>;

    const { memberStats, haesStats, moveStats, bookingStats, topMoves, attendanceStats } = data;

    return (
        <div>
            <CustomNavbar />
            <div className="container mt-4">
                <div className="d-flex align-items-center justify-content-between mb-1 flex-wrap gap-2">
                    <h2 className="mb-0">Statistiken</h2>
                    <div className="d-flex gap-2">
                        <button
                            className="btn btn-outline-warning btn-sm"
                            onClick={() => downloadReport(year)}
                            disabled={reportLoading}
                        >
                            {reportLoading ? '…' : '📥'} Jahresbericht {year} (PDF)
                        </button>
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => downloadReport(year - 1)}
                            disabled={reportLoading}
                        >
                            {year - 1}
                        </button>
                    </div>
                </div>
                <p className="text-secondary mb-4">Saison {year}</p>

                <div className="row g-3">
                    <div className="col-12 col-md-6">
                        <StatCard title={cfg.memberTermPlural}>
                            <div className="row text-center">
                                <div className="col">
                                    <div style={{ fontSize: 32, fontWeight: 700 }}>{memberStats.total}</div>
                                    <div className="text-white-50 small">Gesamt</div>
                                </div>
                                <div className="col">
                                    <div style={{ fontSize: 32, fontWeight: 700, color: '#4ade80' }}>{memberStats.active}</div>
                                    <div className="text-white-50 small">Aktiv</div>
                                </div>
                                <div className="col">
                                    <div style={{ fontSize: 32, fontWeight: 700, color: '#f87171' }}>{memberStats.inactive}</div>
                                    <div className="text-white-50 small">Inaktiv</div>
                                </div>
                            </div>
                        </StatCard>
                    </div>

                    <div className="col-12 col-md-6">
                        <StatCard title={cfg.costumeTermPlural}>
                            {haesStats.length === 0 ? (
                                <p className="text-white-50 small">Keine {cfg.costumeTermPlural} gefunden</p>
                            ) : (
                                <ul className="list-unstyled mb-0">
                                    {haesStats.map(h => (
                                        <li key={h.typ} className="d-flex justify-content-between py-1 border-bottom border-secondary">
                                            <span>{h.typ}</span>
                                            <strong>{h.count}</strong>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </StatCard>
                    </div>

                    <div className="col-12 col-md-6">
                        <StatCard title={`${cfg.eventTermPlural} ${year}`}>
                            <div className="row text-center">
                                <div className="col">
                                    <div style={{ fontSize: 32, fontWeight: 700 }}>{moveStats.movesThisYear || 0}</div>
                                    <div className="text-white-50 small">Dieses Jahr</div>
                                </div>
                                <div className="col">
                                    <div style={{ fontSize: 32, fontWeight: 700 }}>{moveStats.totalMoves}</div>
                                    <div className="text-white-50 small">Gesamt</div>
                                </div>
                            </div>
                        </StatCard>
                    </div>

                    <div className="col-12 col-md-6">
                        <StatCard title={`Buchungen ${year}`}>
                            <div className="row text-center">
                                <div className="col">
                                    <div style={{ fontSize: 32, fontWeight: 700, color: '#4ade80' }}>{bookingStats?.confirmed || 0}</div>
                                    <div className="text-white-50 small">Bestätigt</div>
                                </div>
                                <div className="col">
                                    <div style={{ fontSize: 32, fontWeight: 700, color: '#facc15' }}>{bookingStats?.waitingList || 0}</div>
                                    <div className="text-white-50 small">Warteliste</div>
                                </div>
                                <div className="col">
                                    <div style={{ fontSize: 32, fontWeight: 700 }}>{bookingStats?.total || 0}</div>
                                    <div className="text-white-50 small">Gesamt</div>
                                </div>
                            </div>
                        </StatCard>
                    </div>

                    <div className="col-12">
                        <StatCard title={`Top ${cfg.eventTermPlural} ${year} (nach Buchungen)`}>
                            {topMoves.length === 0 ? (
                                <p className="text-white-50 small">Keine Daten</p>
                            ) : (
                                <table className="table table-dark table-sm mb-0">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Ort</th>
                                            <th>Datum</th>
                                            <th className="text-end">Buchungen</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topMoves.map((m, i) => (
                                            <tr key={i}>
                                                <td className="text-secondary">{i + 1}</td>
                                                <td>{m.city}</td>
                                                <td>{moment(m.date).format('DD.MM.YYYY')}</td>
                                                <td className="text-end fw-bold">{m.bookings}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </StatCard>
                    </div>

                    {/* Anwesenheitsquote */}
                    <div className="col-12">
                        <StatCard title={`Anwesenheitsquote ${cfg.memberTermPlural} ${year}`}>
                            {!attendanceStats || attendanceStats.length === 0 ? (
                                <p className="text-white-50 small">Keine Daten vorhanden (noch keine Anwesenheiten erfasst)</p>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <table className="table table-dark table-sm mb-0">
                                        <thead>
                                            <tr>
                                                <th>{cfg.memberTerm}</th>
                                                <th className="text-center">Buchungen</th>
                                                <th className="text-center">Anwesend</th>
                                                <th className="text-end">Quote</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {attendanceStats.map(m => (
                                                <tr key={m.ID}>
                                                    <td>{m.name}</td>
                                                    <td className="text-center">{m.totalBookings || 0}</td>
                                                    <td className="text-center">{m.attended || 0}</td>
                                                    <td className="text-end">
                                                        {m.attendanceRate !== null && m.attendanceRate !== undefined ? (
                                                            <span style={{
                                                                color: m.attendanceRate >= 70 ? '#4ade80' : m.attendanceRate >= 40 ? '#facc15' : '#f87171',
                                                                fontWeight: 600
                                                            }}>
                                                                {m.attendanceRate}%
                                                            </span>
                                                        ) : <span className="text-white-50">–</span>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </StatCard>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ZRStatistics;
